import React from 'react';
import { useApplication } from '../../context/ApplicationContext';
import FormField from '../FormField';
import SelectField from '../SelectField';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPiggyBank, FiCreditCard, FiTrendingUp, FiMapPin } = FiIcons;

function AccountInfoStep() {
  const { state, dispatch } = useApplication();
  const { formData } = state;

  const handleChange = (field, value) => {
    dispatch({
      type: 'UPDATE_FORM_DATA',
      payload: { [field]: value }
    });
  };

  const accountTypes = [
    {
      value: 'savings',
      label: 'Savings Account',
      icon: FiPiggyBank,
      description: 'Earn interest on your savings with flexible access',
      minDeposit: 'GHS 10',
      features: ['No minimum balance', 'Free ATM withdrawals', 'Mobile banking']
    },
    {
      value: 'current',
      label: 'Current Account',
      icon: FiCreditCard,
      description: 'Perfect for business and frequent transactions',
      minDeposit: 'GHS 100',
      features: ['Unlimited transactions', 'Checkbook facility', 'Overdraft available']
    },
    {
      value: 'investment',
      label: 'Investment Account',
      icon: FiTrendingUp,
      description: 'High-yield account for long-term savings',
      minDeposit: 'GHS 1,000',
      features: ['Higher interest rates', 'Investment advisory', 'Term deposits']
    }
  ];

  const idTypeOptions = [
    { value: 'national-id', label: 'National ID Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'drivers-license', label: 'Driver\'s License' },
    { value: 'voters-id', label: 'Voter\'s ID' },
  ];

  const branchOptions = [
    { value: 'accra-main', label: 'Accra Main Branch' },
    { value: 'kumasi', label: 'Kumasi Branch' },
    { value: 'tamale', label: 'Tamale Branch' },
    { value: 'cape-coast', label: 'Cape Coast Branch' },
    { value: 'ho', label: 'Ho Branch' },
    { value: 'sunyani', label: 'Sunyani Branch' },
    { value: 'koforidua', label: 'Koforidua Branch' },
    { value: 'wa', label: 'Wa Branch' },
  ];

  return (
    <div className="space-y-6 sm:space-y-8 w-full max-w-full overflow-x-hidden">
      {/* Account Type Selection */}
      <div className="w-full">
        <h3 className="text-lg font-semibold text-agric-primary mb-4">Choose Your Account Type</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
          {accountTypes.map((account) => (
            <div
              key={account.value}
              onClick={() => handleChange('accountType', account.value)}
              className={`cursor-pointer rounded-lg border-2 p-4 transition-all duration-300 w-full max-w-full ${
                formData.accountType === account.value
                  ? 'border-agric-primary bg-agric-primary/5'
                  : 'border-gray-200 hover:border-agric-primary/50'
              }`}
            >
              <div className="text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  formData.accountType === account.value
                    ? 'bg-agric-primary text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <SafeIcon icon={account.icon} className="w-6 h-6" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">{account.label}</h4>
                <p className="text-xs sm:text-sm text-gray-600 mb-3">{account.description}</p>
                <p className="text-xs sm:text-sm font-medium text-agric-primary mb-3">
                  Min. Deposit: {account.minDeposit}
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {account.features.map((feature, idx) => (
                    <li key={idx} className="break-words">• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Initial Deposit and Branch */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <FormField
          label="Initial Deposit Amount (GHS)"
          type="number"
          value={formData.initialDeposit}
          onChange={(value) => handleChange('initialDeposit', value)}
          required
          min="10"
          placeholder="Enter amount"
          fieldName="initialDeposit"
        />
        <SelectField
          label="Preferred Branch"
          value={formData.preferredBranch}
          onChange={(value) => handleChange('preferredBranch', value)}
          options={branchOptions}
          required
          icon={FiMapPin}
          fieldName="preferredBranch"
        />
      </div>

      {/* Identification Section */}
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 w-full max-w-full">
        <h3 className="text-lg font-semibold text-agric-primary mb-4">Identification Details</h3>
        <div className="space-y-4">
          <SelectField
            label="ID Type"
            value={formData.idType}
            onChange={(value) => handleChange('idType', value)}
            options={idTypeOptions}
            required
            fieldName="idType"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <FormField
              label="ID Number"
              type="text"
              value={formData.idNumber}
              onChange={(value) => handleChange('idNumber', value)}
              required
              placeholder="Enter ID number"
              fieldName="idNumber"
            />
            <FormField
              label="ID Expiry Date"
              type="date"
              value={formData.idExpiryDate}
              onChange={(value) => handleChange('idExpiryDate', value)}
              required
              fieldName="idExpiryDate"
            />
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 w-full max-w-full">
        <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-4">Terms and Conditions</h3>
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.termsAccepted}
              onChange={(e) => handleChange('termsAccepted', e.target.checked)}
              className="mt-1 w-4 h-4 text-agric-primary border-gray-300 rounded focus:ring-agric-primary flex-shrink-0"
              required
            />
            <span className="text-xs sm:text-sm text-gray-700">
              I have read and agree to the{' '}
              <a href="#" className="text-agric-primary hover:underline">Terms and Conditions</a>{' '}
              and{' '}
              <a href="#" className="text-agric-primary hover:underline">Privacy Policy</a>{' '}
              of Agric Bank Ghana.
            </span>
          </label>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.marketingConsent}
              onChange={(e) => handleChange('marketingConsent', e.target.checked)}
              className="mt-1 w-4 h-4 text-agric-primary border-gray-300 rounded focus:ring-agric-primary flex-shrink-0"
            />
            <span className="text-xs sm:text-sm text-gray-700">
              I consent to receive marketing communications about Agric Bank products and services via email, SMS, and phone calls.
            </span>
          </label>
        </div>
        {state.errors.termsAccepted && (
          <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
            <span className="break-words">{state.errors.termsAccepted}</span>
          </div>
        )}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 w-full max-w-full">
        <h4 className="font-medium text-green-900 mb-2 text-sm sm:text-base">Ready to Submit</h4>
        <p className="text-xs sm:text-sm text-green-800">
          Please review all your information carefully before submitting. Once submitted, your application will be processed within 2-3 business days.
        </p>
      </div>
    </div>
  );
}

export default AccountInfoStep;